import React, { useState } from 'react';
import { Button } from 'devextreme-react/button';
import { RiskMeterDto } from '../../../../dtos/RiskMeterDto';
import { GrantFinancialDto } from '../../../../dtos/GrantFinancialDto';
import { RiskMeterDisplayProps } from '../types/riskMeterTypes';
import RiskMeterChart from './RiskMeterChart';
import RiskMeterTable from './RiskMeterTable';
import RiskMeterFinancialTable from './RiskMeterFinancialTable';
import RiskMeterLegend from './RiskMeterLegend';
import FinancialEditDialog from './FinancialEditDialog';

interface RiskMeterViewProps extends RiskMeterDisplayProps {
    data: RiskMeterDto;
    screenshotTitle?: string;
    containerRef?: React.RefObject<HTMLDivElement>;
    exportAsPng?: () => Promise<void>;
    grantId?: string;
    financial?: GrantFinancialDto | null;
    reload?: () => void;
}

export default function RiskMeterView({
    data,
    variant = 'full',
    height,
    width,
    showLabels = true,
    showConnectors = true,
    showTooltip = true,
    showCenterCircle = true,
    showBackground = false,
    showTable = true,
    showLegend = true,
    screenshotTitle,
    containerRef,
    exportAsPng,
    grantId,
    financial,
    reload
}: RiskMeterViewProps) {
    const [isEditingFinancial, setIsEditingFinancial] = useState(false);

    // If only showing chart (icon mode), render just the chart
    if (!showTable && !showLegend) {
        return (
            <RiskMeterChart
                data={data}
                variant={variant}
                height={height}
                width={width}
                showLabels={showLabels}
                showConnectors={showConnectors}
                showTooltip={showTooltip}
                showCenterCircle={showCenterCircle}
                showBackground={showBackground}
            />
        );
    }

    // Full view with table, legend, and chart
    return (
        <div style={{ position: 'relative' }}>
            {screenshotTitle && exportAsPng && (
                <Button
                    className="ganttLabelButton"
                    style={{ position: 'absolute', top: 4, right: 20, zIndex: 1 }}
                    width={100}
                    height={25}
                    text="Export PNG"
                    type="normal"
                    stylingMode="contained"
                    onClick={exportAsPng}
                />
            )}
            <div ref={containerRef} className="container-fluid panelBody" style={{ marginTop: '20px', paddingLeft: '20px', paddingRight: '20px' }}>
                <div className="row">
                    <div className="col-6">
                        {showTable && <RiskMeterTable data={data} />}
                    </div>
                    <div className="col-6">
                        <RiskMeterChart
                            data={data}
                            variant={variant}
                            height={height}
                            width={width}
                            showLabels={showLabels}
                            showConnectors={showConnectors}
                            showTooltip={showTooltip}
                            showCenterCircle={showCenterCircle}
                            showBackground={showBackground}
                        />
                    </div>
                </div>
                {data.financial && (
                    <div style={{ marginTop: '20px' }}>
                        <RiskMeterFinancialTable
                            financial={data.financial}
                            canEdit={financial?.canEdit}
                            onEdit={() => setIsEditingFinancial(true)}
                        />
                    </div>
                )}
                {showLegend && (
                    <div style={{ marginTop: '20px' }}>
                        <RiskMeterLegend />
                    </div>
                )}
            </div>

            {grantId && financial && (
                <FinancialEditDialog
                    grantId={grantId}
                    financial={financial}
                    visible={isEditingFinancial}
                    onClose={() => setIsEditingFinancial(false)}
                    onSaved={() => reload?.()}
                />
            )}
        </div>
    );
}
